import { ErrorBoundary } from '@ibe/ui-web';
import { Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { NotFoundPage } from './not-found-page.js';
import { LoadingScreen } from '../shared/components/loading-screen.js';
import { SiteLayout } from '../shared/layout/site-layout.js';
import { useTranslation } from '../shared/use-translation.js';

const HomePage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/home-page.js')).HomePage,
}));
const SearchPage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/search-page.js')).SearchPage,
}));
const RoomRatePage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/room-rate-page.js')).RoomRatePage,
}));
const RoomInfoPage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/room-info-page.js')).RoomInfoPage,
}));
const CheckoutPage = lazy(async () => ({
  default: (await import('../features/booking/pages/checkout-page.js')).CheckoutPage,
}));
const GuestDetailsPage = lazy(async () => ({
  default: (await import('../features/booking/pages/guest-details-page.js')).GuestDetailsPage,
}));
const PaymentPage = lazy(async () => ({
  default: (await import('../features/booking/pages/payment-page.js')).PaymentPage,
}));
const ConfirmationPage = lazy(async () => ({
  default: (await import('../features/booking/pages/confirmation-page.js')).ConfirmationPage,
}));
const BookingLookupPage = lazy(async () => ({
  default: (await import('../features/booking-management/pages/booking-lookup-page.js'))
    .BookingLookupPage,
}));
const AuthIndexPage = lazy(async () => ({
  default: (await import('../features/auth/pages/auth-index-page.js')).AuthIndexPage,
}));
const SignInPage = lazy(async () => ({
  default: (await import('../features/auth/pages/sign-in-page.js')).SignInPage,
}));
const SignUpPage = lazy(async () => ({
  default: (await import('../features/auth/pages/sign-up-page.js')).SignUpPage,
}));
const ForgotPasswordPage = lazy(async () => ({
  default: (await import('../features/auth/pages/forgot-password-page.js')).ForgotPasswordPage,
}));
const ResetPasswordPage = lazy(async () => ({
  default: (await import('../features/auth/pages/reset-password-page.js')).ResetPasswordPage,
}));
const VerifyEmailPage = lazy(async () => ({
  default: (await import('../features/auth/pages/verify-email-page.js')).VerifyEmailPage,
}));
const BookingDetailsPage = lazy(async () => ({
  default: (await import('../features/booking-management/pages/booking-details-page.js'))
    .BookingDetailsPage,
}));
const CancellationPage = lazy(async () => ({
  default: (await import('../features/booking-management/pages/cancellation-page.js'))
    .CancellationPage,
}));
const ReservationDetailPage = lazy(async () => ({
  default: (await import('../features/booking/pages/reservation-detail-page.js'))
    .ReservationDetailPage,
}));
const LegacyRedirectPage = lazy(async () => ({
  default: (await import('../features/catalogue/pages/legacy-redirect-page.js')).LegacyRedirectPage,
}));

export function AppRoutes(): React.JSX.Element {
  const location = useLocation();
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route
          path="/"
          element={
            <Boundary key={location.pathname}>
              <HomePage />
            </Boundary>
          }
        />
        <Route
          path="/search"
          element={
            <Boundary key={location.pathname}>
              <SearchPage />
            </Boundary>
          }
        />
        <Route
          path="/properties/:propertyId"
          element={
            <Boundary key={location.pathname}>
              <RoomRatePage />
            </Boundary>
          }
        />
        <Route
          path="/cllix/:propertyId"
          element={
            <Boundary key={location.pathname}>
              <LegacyRedirectPage />
            </Boundary>
          }
        />
        <Route
          path="/properties/:propertyId/rooms/:roomTypeId"
          element={
            <Boundary key={location.pathname}>
              <RoomInfoPage />
            </Boundary>
          }
        />
        <Route
          path="/room-rate/:propertyId"
          element={
            <Boundary key={location.pathname}>
              <RoomRatePage />
            </Boundary>
          }
        />
        <Route
          path="/checkout"
          element={
            <Boundary key={location.pathname}>
              <CheckoutPage />
            </Boundary>
          }
        />
        <Route
          path="/room-rate/:propertyId/:roomTypeId"
          element={
            <Boundary key={location.pathname}>
              <RoomInfoPage />
            </Boundary>
          }
        />
        <Route
          path="/room-info/:propertyId/:roomTypeId"
          element={
            <Boundary key={location.pathname}>
              <RoomInfoPage />
            </Boundary>
          }
        />
        <Route
          path="/guest-details"
          element={
            <Boundary key={location.pathname}>
              <GuestDetailsPage />
            </Boundary>
          }
        />
        <Route
          path="/payment"
          element={
            <Boundary key={location.pathname}>
              <PaymentPage />
            </Boundary>
          }
        />
        <Route
          path="/confirmation/:bookingReference"
          element={
            <Boundary key={location.pathname}>
              <ConfirmationPage />
            </Boundary>
          }
        />
        <Route
          path="/reservation/:bookingReference"
          element={
            <Boundary key={location.pathname}>
              <ReservationDetailPage />
            </Boundary>
          }
        />
        <Route
          path="/lookup"
          element={
            <Boundary key={location.pathname}>
              <BookingLookupPage />
            </Boundary>
          }
        />
        <Route
          path="/auth"
          element={
            <Boundary key={location.pathname}>
              <AuthIndexPage />
            </Boundary>
          }
        />
        <Route
          path="/auth/sign-in"
          element={
            <Boundary key={location.pathname}>
              <SignInPage />
            </Boundary>
          }
        />
        <Route
          path="/auth/sign-up"
          element={
            <Boundary key={location.pathname}>
              <SignUpPage />
            </Boundary>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Boundary key={location.pathname}>
              <ForgotPasswordPage />
            </Boundary>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Boundary key={location.pathname}>
              <ResetPasswordPage />
            </Boundary>
          }
        />
        <Route
          path="/verify-email"
          element={
            <Boundary key={location.pathname}>
              <VerifyEmailPage />
            </Boundary>
          }
        />
        <Route
          path="/booking/:bookingReference"
          element={
            <Boundary key={location.pathname}>
              <BookingDetailsPage />
            </Boundary>
          }
        />
        <Route
          path="/cancel/:bookingReference"
          element={
            <Boundary key={location.pathname}>
              <CancellationPage />
            </Boundary>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function Boundary({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const location = useLocation();
  const { t } = useTranslation();
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingScreen fullScreen label={t('common.loading')} />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}
